/**
 * Refusals of a Google sign-in.
 *
 * A refused sign-in does not arrive as a response body. Cognito sends the
 * browser back to the app's OAuth return path with the code buried in a
 * sentence:
 *
 *   /auth?error=invalid_request
 *        &error_description=PreSignUp%20failed%20with%20error%20error.social_link_required.%20
 *
 * The sentence, the trailing full stop and the trailing space are all part of
 * what Cognito sends, so the code has to be matched as a substring - never by
 * comparing the whole parameter.
 */
export type SocialSignInRefusal =
  | 'social_link_required'
  | 'social_already_linked'
  | 'social_another_linked'
  | 'not_allowed_email';

/** The literal each refusal appears as inside `error_description`. */
export const SOCIAL_SIGN_IN_REFUSAL_CODES: Readonly<Record<SocialSignInRefusal, string>> = {
  // The Google account is fine, we simply do not know this person. The only
  // refusal the customer can do something about: the contract-email modal.
  social_link_required: 'error.social_link_required',

  // The Google account already signs in to another portal account.
  social_already_linked: 'error.social_already_linked',

  // The portal account on this address already carries a different Google
  // account. One account, one Google - the rule Connect enforces on the
  // profile page, enforced by the backend for the sign-in as well.
  social_another_linked: 'error.social_another_linked',

  // No contract on the address, or Google sent nothing we can use.
  not_allowed_email: 'error.not_allowed_email'
};

export const SOCIAL_SIGN_IN_REFUSALS =
  Object.keys(SOCIAL_SIGN_IN_REFUSAL_CODES) as readonly SocialSignInRefusal[];

/**
 * The message each refusal is shown with, under `auth`.
 *
 * `social_link_required` has no entry: it opens the contract-email modal
 * instead of putting a message on the login screen.
 */
export const SOCIAL_SIGN_IN_MESSAGE_KEYS: Readonly<Partial<Record<SocialSignInRefusal, string>>> = {
  social_already_linked: 'auth.social-sign-in.errors.already-linked',
  // The Connect flow's copy says exactly what this customer has to do: sign in
  // with the password, then disconnect the other Google account in the profile.
  social_another_linked: 'auth.google-link.errors.another-google-linked',
  not_allowed_email: 'auth.sign-up.errors.email-not-in-mfv'
};

/** A refused sign-in, read off the OAuth return path. */
export interface SocialSignInRefusalResult {
  /** Only set when the description carried a code we know. */
  refusal?: SocialSignInRefusal;
  /** The description exactly as it arrived. Kept for the console, never shown. */
  rawDescription?: string;
}
